import { TEAM_COMMAND_IDS, type TeamCommandId } from './game';

export type TacticalPingKind =
  | 'enemy-spotted'
  | 'need-repair'
  | 'vehicle-here'
  | 'route-safe'
  | 'route-danger';

const TACTICAL_PING_KINDS: TacticalPingKind[] = [
  'enemy-spotted',
  'need-repair',
  'vehicle-here',
  'route-safe',
  'route-danger',
];

export { TACTICAL_PING_KINDS };

const ROUTE_ZONE_CALLSIGNS: Record<string, string> = {
  'route-mid': 'MID CUT',
  'route-red-approach': 'RED FLANK',
  'route-blue-approach': 'BLUE FLANK',
  'route-high': 'HIGH LINE',
  'route-low': 'LOW LINE',
  'red-main': 'RED MAIN',
  'blue-main': 'BLUE MAIN',
  'red-high': 'RED HIGH',
  'blue-high': 'BLUE HIGH',
  'red-low': 'RED LOW',
  'blue-chase': 'BLUE CHASE',
  'generator-attack': 'GEN CUT',
  'red-heavy': 'HEAVY LANE',
  'dc-red-main': 'RED CANYON',
  'dc-blue-main': 'BLUE CANYON',
  'dc-canyon-high': 'CANYON HIGH',
  'dc-canyon-low': 'CANYON LOW',
  'dc-blue-chase': 'BLUE CHASE',
  'dc-generator-attack': 'GEN CUT',
  'dc-heavy-lane': 'HEAVY LANE',
  'dc-return-route': 'RETURN ROUTE',
};

const ROUTE_ZONE_LABELS: Record<string, string> = {
  'route-mid': 'Mid Cut',
  'route-red-approach': 'Red Flank',
  'route-blue-approach': 'Blue Flank',
  'route-high': 'High Line',
  'route-low': 'Low Line',
  'red-main': 'Red Main',
  'blue-main': 'Blue Main',
  'red-high': 'Red High',
  'blue-high': 'Blue High',
  'red-low': 'Red Low',
  'blue-chase': 'Blue Chase',
  'generator-attack': 'Generator Attack',
  'red-heavy': 'Red Heavy',
  'dc-red-main': 'Red Canyon',
  'dc-blue-main': 'Blue Canyon',
  'dc-canyon-high': 'Canyon High',
  'dc-canyon-low': 'Canyon Low',
  'dc-blue-chase': 'Blue Chase',
  'dc-generator-attack': 'Generator Attack',
  'dc-heavy-lane': 'Heavy Lane',
  'dc-return-route': 'Return Route',
};

function normalizeText(value: string) {
  return value
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function normalizeTeamCommand(value: unknown): TeamCommandId | null {
  if (value === 'support') {
    return 'escort';
  }

  return TEAM_COMMAND_IDS.find((entry) => entry === value) ?? null;
}

export function normalizePingRequest(value: unknown): boolean {
  return value === true;
}

export function normalizeTacticalPingKind(value: unknown): TacticalPingKind | null {
  if (typeof value !== 'string') {
    return null;
  }

  return TACTICAL_PING_KINDS.find((entry) => entry === value) ?? null;
}

export function routeZoneCallsign(routeZoneId?: string | null): string {
  if (!routeZoneId) {
    return 'ROUTE';
  }

  return ROUTE_ZONE_CALLSIGNS[routeZoneId] ?? 'ROUTE';
}

export function routeZoneLabel(routeZoneId?: string | null): string {
  if (!routeZoneId) {
    return 'Route';
  }

  return ROUTE_ZONE_LABELS[routeZoneId] ?? 'Route';
}

export function summarizeDirectiveCall(command?: string | null, routeZoneId?: string | null): string | null {
  if (!command) {
    return null;
  }

  const summary = normalizeText(command);
  return `${summary} · ${routeZoneLabel(routeZoneId)}`;
}

export function commandBriefing(command: TeamCommandId, routeZoneId?: string): string {
  const lane = routeZoneLabel(routeZoneId);
  if (command === 'attack') {
    return `Attack order: push ${lane}`;
  }
  if (command === 'defend') {
    return `Defend order: hold ${lane}`;
  }
  if (command === 'escort') {
    return `Escort order: screen ${lane}`;
  }
  if (command === 'regroup') {
    return `Regroup order: rally ${lane}`;
  }
  if (command === 'repair') {
    return `Repair order: restore assets on ${lane}`;
  }
  if (command === 'deploy') {
    return `Deploy order: place deployables on ${lane}`;
  }
  if (command === 'vehicle') {
    return `Vehicle order: request transport near ${lane}`;
  }
  if (command === 'generator') {
    return `Generator order: defend or assault generator at ${lane}`;
  }
  if (command === 'sensor') {
    return `Sensor order: sweep or place sensors on ${lane}`;
  }
  if (command === 'flag-request') {
    return `Flag request: request flag status near ${lane}`;
  }
  return `Command order: ${normalizeText(command)} on ${lane}`;
}

export function commandEventPriority(command: TeamCommandId): number {
  if (command === 'attack' || command === 'defend') {
    return 340;
  }
  if (command === 'escort') {
    return 300;
  }
  if (command === 'generator' || command === 'sensor' || command === 'flag-request') {
    return 380;
  }
  if (command === 'repair') {
    return 290;
  }
  if (command === 'deploy') {
    return 270;
  }
  if (command === 'vehicle') {
    return 260;
  }
  return 260;
}

export function tacticalPingBriefing(kind?: TacticalPingKind | string, routeZoneId?: string, objectiveId?: string): string {
  const lane = routeZoneLabel(routeZoneId);

  if (kind === 'enemy-spotted') {
    return `Ping: enemy spotted on ${lane}`;
  }
  if (kind === 'need-repair') {
    return `Ping: repair needed on ${lane}`;
  }
  if (kind === 'vehicle-here') {
    return `Ping: vehicle available near ${lane}`;
  }
  if (kind === 'route-safe') {
    return `Ping: route clear on ${lane}`;
  }
  if (kind === 'route-danger') {
    return `Ping: route dangerous on ${lane}`;
  }

  if (objectiveId) {
    return `Ping: objective call on ${lane}`;
  }
  return `Ping: pressure check on ${lane}`;
}
